import { useSyncExternalStore } from 'react';
import { UserGroup, PaginatedResponse } from '@/types';
import { api } from '@/services/api';
import { useStaffDataContext } from './StaffDataContext';

export interface UserGroupListParams {
  page: number;
  pageSize: number;
  q?: string;
}

const buildQuery = (params: UserGroupListParams) => ({
  page: params.page,
  pageSize: params.pageSize,
  q: params.q || undefined
});

export const useUserGroups = (params: UserGroupListParams): PaginatedResponse<UserGroup> => {
  const ctx = useStaffDataContext();
  useSyncExternalStore(
    (cb) => ctx.subscribe('userGroups', cb),
    () => 0,
    () => 0
  );
  return ctx.read('userGroups', params, (p: UserGroupListParams) =>
    api.listStaffUserGroups(buildQuery(p))
  );
};

export interface UserGroupMutations {
  create: (group: UserGroup) => Promise<void>;
  update: (group: UserGroup) => Promise<void>;
  remove: (groupId: string) => Promise<void>;
}

export const useUserGroupMutations = (): UserGroupMutations => {
  const ctx = useStaffDataContext();
  // userGroup mutations may also affect users (when a user's group is deleted/edited).
  const after = () => {
    ctx.invalidate('userGroups');
    ctx.invalidate('users');
  };
  return {
    create: async (group) => {
      await api.createUserGroup(group);
      after();
    },
    update: async (group) => {
      await api.updateUserGroup(group);
      after();
    },
    remove: async (groupId) => {
      await api.deleteUserGroup(groupId);
      after();
    }
  };
};
